<template>
  <div class="q-gutter-md">
    <property-report-card
      title="5-Year Rent Forecast"
      description="Projected rent growth for this property is based on HelloData's dynamic forecasting model."
      :loading="isLoading"
      :printing-mode="printingMode"
    >
      <template #actions>
        <q-select
          v-model="metric"
          :disable="isLoading"
          :options="metricOptions"
          dense
          emit-value
          label="Metric"
          map-options
          outlined
          style="width: 150px"
          transition-duration="100"
          @update:model-value="(val) => $tracker.changedFilter('metric', 'rent_projections', val)"
        />
        <q-select
          v-model="frequency"
          :disable="isLoading"
          :options="frequencyOptions"
          dense
          emit-value
          label="Frequency"
          map-options
          outlined
          style="width: 150px"
          transition-duration="100"
          @update:model-value="
            (val) => $tracker.changedFilter('frequency', 'rent_projections', val)
          "
        />
      </template>
      <q-separator />
      <rent-projections-chart
        :is-single-property="true"
        :metric="metric"
        :frequency="frequency"
        :subject-property="subjectProperty"
      >
        <template v-if="!subjectProperty" #empty-state>
          <div
            class="full-width full-height flex flex-center text-center text-primary"
            style="font-size: 12px"
          >
            No data found for Subject Property or missing Subject Property.
          </div>
        </template>
      </rent-projections-chart>
      <q-card-section class="q-gutter-y-sm">
        <div class="text-weight-bold">How Our Forecasts Work</div>
        <div style="font-size: 12px">
          HelloData's rent projections are generated using a deep-learning model designed
          specifically for multifamily markets. The model captures how supply, demand, amenities,
          and neighboring submarkets interact over time.
        </div>
        <div style="font-size: 12px">
          Download our
          <a
            href="https://storage.googleapis.com/hello-data-ai-public-assets/HelloData%20-%20Rent%20Forecasts%20Overview.pdf"
            class="text-primary"
            target="_blank"
          >
            Full Rent Forecast Model Overview
          </a>
        </div>
      </q-card-section>
    </property-report-card>
  </div>
</template>

<script lang="ts">
import { type PropType, computed } from 'vue'
import type { PropertyDetailsResponse } from 'common'
import { useSinglePropertyRentProjection } from '@/queries/rentProjections.ts'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import RentProjectionsChart from '@/components/Charts/RentProjectionsChart.vue'

export default {
  name: 'PropertyCompareRentProjections',
  props: {
    subjectProperty: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  components: {
    PropertyReportCard,
    RentProjectionsChart
  },
  data() {
    return {
      frequency: 'yearly' as 'yearly' | 'monthly',
      frequencyOptions: [
        { label: 'Yearly', value: 'yearly' },
        { label: 'Monthly', value: 'monthly' }
      ],
      metric: 'percent' as 'percent' | 'dollar',
      metricOptions: [
        {
          label: 'Percent (%)',
          value: 'percent'
        },
        {
          label: 'Dollar ($)',
          value: 'dollar'
        }
      ]
    }
  },
  setup(props) {
    const lat = computed(() => props.subjectProperty?.lat ?? null)
    const lng = computed(() => props.subjectProperty?.lon ?? null)
    const isLeaseUp = computed(() => props.subjectProperty?.is_lease_up ?? false)
    const { isLoading } = useSinglePropertyRentProjection(lat, lng, isLeaseUp)
    return { isLoading }
  }
}
</script>
